/**
 * AdminIndirectClaims — Vinci Admin console tab.
 *
 * The ONLY channel through which past / off-platform Purchase Orders
 * can be converted into a Vinci commission claim. Buyers cannot
 * self-file this — admin must author every entry and every entry
 * carries a mandatory Vinci Trade Desk approval gate.
 *
 * Live slab preview shows the % payout reduction based on the gap
 * between the fulfillment date and the applying date:
 *   0 – 15 d  · 100 %       (no reduction)
 *   16 – 30 d ·  80 %
 *   31 – 45 d ·  50 %
 *   > 45 d    ·   0 %  →  Level-1 Board escalation required.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ScrutinyControl, EvidenceCallPanel } from "./AdminClaimScrutiny";
import {
  AlertTriangle, ShieldCheck, XCircle, Send, Calendar, RefreshCw,
  ArrowDownRight, Info,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN",
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);

function SlabBadge({ pct, label }) {
  const c = pct >= 100 ? "#2FB999"
          : pct >= 80  ? "#C9A961"
          : pct >= 50  ? "#F5D67A"
          : "#C0392B";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.28em]"
          style={{ color: c, borderColor: `${c}55`, background: `${c}0F` }}>
      {label} · {pct.toFixed(0)}%
    </span>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:  ["#F5D67A", "Pending Trade Desk"],
    approved: ["#2FB999", "Approved"],
    rejected: ["#C0392B", "Rejected"],
  };
  const [c, l] = map[status] || ["#8A8A82", status || "—"];
  return <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.26em]"
    style={{ color: c, borderColor: `${c}55` }}>{l}</span>;
}

function AckPill({ ack }) {
  if (ack) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#5EE6D0] border-[#5EE6D0]/50"
            data-testid="ack-pill-verified">
        Ack ✓
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#B87333] border-[#B87333]/50"
          data-testid="ack-pill-pending">
      Awaiting buyer ack
    </span>
  );
}

// ───────── Composer form ─────────
function Composer({ api, onCreated, policy }) {
  const [f, setF] = useState({
    buyer_email: "", order_ref: "", order_value: "", base_rate_pct: "1.0",
    fulfillment_date: "", applying_date: today(), material: "",
    quantity: "", project_name: "", supplier_name: "", supplier_gstin: "",
    note: "", force_beyond_slab: false,
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const canPreview = f.order_value && f.base_rate_pct && f.fulfillment_date;

  useEffect(() => {
    setPreview(null); setError("");
    if (!canPreview) return;
    const h = setTimeout(async () => {
      try {
        const { data } = await api.post("/admin/indirect-claims/preview", {
          order_value: Number(f.order_value),
          base_rate_pct: Number(f.base_rate_pct),
          fulfillment_date: f.fulfillment_date,
          applying_date: f.applying_date || null,
        });
        setPreview(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not compute preview.");
      }
    }, 220);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.order_value, f.base_rate_pct, f.fulfillment_date, f.applying_date]);

  const submit = async () => {
    if (!f.buyer_email || !f.order_ref) { toast.error("Buyer email + order ref are required."); return; }
    if (!preview) { toast.error("Fill order value, rate & fulfillment date first."); return; }
    if (preview.requires_board_escalation && !f.force_beyond_slab) {
      toast.error("Beyond 45 days — tick 'Escalate to Board' to force-insert.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/indirect-claims", {
        ...f,
        order_value: Number(f.order_value),
        base_rate_pct: Number(f.base_rate_pct),
        applying_date: f.applying_date || null,
      });
      toast.success("Indirect claim created — Trade Desk notified.");
      setF({ ...f, buyer_email: "", order_ref: "", order_value: "",
              fulfillment_date: "", material: "", quantity: "",
              project_name: "", supplier_name: "", supplier_gstin: "",
              note: "", force_beyond_slab: false });
      setPreview(null);
      onCreated(data.claim);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Create failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-sm border border-[#C0392B]/40 bg-gradient-to-br from-[#1a0d0a] via-[#0f0908] to-[#0a0606] p-6 mb-6" data-testid="indirect-composer">
      <div className="flex items-start gap-3">
        <AlertTriangle size={24} className="text-[#C0392B] mt-0.5" />
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C0392B]">Indirect (Past-PO) Commission — Admin Author</div>
          <h2 className="font-serif text-2xl text-[#F5F5F0]">Insert an off-platform PO claim under Vinci Trade Desk supervision</h2>
          <p className="font-body text-[13px] text-[#F5F5F0] mt-2 max-w-3xl leading-relaxed">
            <b className="text-[#C0392B]">Special approval required by Vinci Trade Desk.</b>{" "}
            {policy?.warning || "Payouts are reduced when the fulfillment-to-applying gap exceeds 15 days."}
          </p>
        </div>
      </div>

      <div className="mt-5 grid md:grid-cols-3 gap-3">
        <FormField label="Buyer email">
          <input type="email" value={f.buyer_email} onChange={(e) => setF({ ...f, buyer_email: e.target.value })}
            data-testid="ic-buyer" className="input" />
        </FormField>
        <FormField label="PO / order ref">
          <input value={f.order_ref} onChange={(e) => setF({ ...f, order_ref: e.target.value })}
            data-testid="ic-order-ref" className="input" placeholder="PO-2025-1234" />
        </FormField>
        <FormField label="Order value (INR)">
          <input type="number" min="0" step="0.01" value={f.order_value}
            onChange={(e) => setF({ ...f, order_value: e.target.value })}
            data-testid="ic-order-value" className="input" />
        </FormField>
        <FormField label="Base commission %">
          <input type="number" min="0.01" step="0.01" max="25" value={f.base_rate_pct}
            onChange={(e) => setF({ ...f, base_rate_pct: e.target.value })}
            data-testid="ic-rate" className="input" />
        </FormField>
        <FormField label="Fulfillment date">
          <input type="date" value={f.fulfillment_date}
            onChange={(e) => setF({ ...f, fulfillment_date: e.target.value })}
            data-testid="ic-fulfill" className="input" />
        </FormField>
        <FormField label="Applying date">
          <input type="date" value={f.applying_date}
            onChange={(e) => setF({ ...f, applying_date: e.target.value })}
            data-testid="ic-apply" className="input" />
        </FormField>
        <FormField label="Material"><input value={f.material} onChange={(e) => setF({ ...f, material: e.target.value })} data-testid="ic-material" className="input" /></FormField>
        <FormField label="Quantity"><input value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="ic-qty" className="input" /></FormField>
        <FormField label="Project"><input value={f.project_name} onChange={(e) => setF({ ...f, project_name: e.target.value })} data-testid="ic-project" className="input" /></FormField>
        <FormField label="Supplier"><input value={f.supplier_name} onChange={(e) => setF({ ...f, supplier_name: e.target.value })} data-testid="ic-supplier" className="input" /></FormField>
        <FormField label="Supplier GSTIN"><input value={f.supplier_gstin} onChange={(e) => setF({ ...f, supplier_gstin: e.target.value.toUpperCase() })} data-testid="ic-gstin" className="input" /></FormField>
        <FormField label="Admin note">
          <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })}
            data-testid="ic-note" className="input" placeholder="Why is this being inserted manually?" />
        </FormField>
      </div>

      {/* Live slab preview */}
      {preview && (
        <div className="mt-6 rounded-sm border p-4 grid md:grid-cols-4 gap-4"
          style={{
            borderColor: preview.requires_board_escalation ? "#C0392B70" : "#C9A96140",
            background: preview.requires_board_escalation ? "#C0392B10" : "#C9A9610C",
          }}
          data-testid="ic-preview">
          <div>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">Days gap</div>
            <div className="font-serif text-3xl text-[#F5F5F0] tabular-nums mt-1" data-testid="ic-preview-gap">
              {preview.days_gap} d
            </div>
            <div className="mt-2"><SlabBadge pct={preview.slab.payout_pct} label={preview.slab.label} /></div>
          </div>
          <div>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">Eligible</div>
            <div className="font-serif text-xl text-[#F5F5F0] tabular-nums mt-1">{inr(preview.eligible_commission_inr)}</div>
            <div className="font-body text-[11px] text-[#8A8A82]">@ {preview.base_rate_pct}% of PO</div>
          </div>
          <div>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">Reduction</div>
            <div className="font-serif text-xl text-[#C0392B] tabular-nums mt-1 flex items-center gap-1" data-testid="ic-preview-deduction">
              <ArrowDownRight size={16} />{inr(preview.deduction_inr)}
            </div>
          </div>
          <div>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">Payable</div>
            <div className="font-serif text-2xl tabular-nums mt-1"
              style={{ color: preview.slab.payout_pct > 0 ? "#2FB999" : "#C0392B" }}
              data-testid="ic-preview-payable">
              {inr(preview.payable_commission_inr)}
            </div>
          </div>
        </div>
      )}

      {preview?.requires_board_escalation && (
        <label className="mt-4 flex items-start gap-2 rounded-sm border border-[#C0392B]/60 bg-black/50 p-3 cursor-pointer" data-testid="ic-force-wrap">
          <input type="checkbox" checked={f.force_beyond_slab}
            onChange={(e) => setF({ ...f, force_beyond_slab: e.target.checked })}
            data-testid="ic-force" className="mt-1" />
          <span className="font-body text-[13px] text-[#F5F5F0] leading-relaxed">
            <b className="text-[#C0392B]">Escalate to Level-1 Executive Board.</b> Force-insert this claim as a 0% payout on record. Board must issue a special sanction before any commission can be released.
          </span>
        </label>
      )}

      {error && <div className="mt-3 text-[13px] text-[#C0392B] font-body">{error}</div>}

      <div className="mt-5 flex items-center gap-3">
        <button onClick={submit} disabled={busy || !preview}
          data-testid="ic-submit"
          className="rounded-sm bg-[#C9A961] hover:bg-[#F5D67A] text-black px-6 py-2.5 font-grotesk text-[11px] uppercase tracking-[0.28em] transition-colors disabled:opacity-40">
          <Send size={13} className="inline mr-2 -mt-0.5" />
          {busy ? "Creating…" : "Create indirect claim"}
        </button>
        <span className="font-body text-[11px] text-[#8A8A82] inline-flex items-center gap-1.5">
          <Info size={11} /> Trade Desk approval will still be required before payout
        </span>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.25em] text-[#8A8A82]">{label}</div>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// ───────── List + Trade Desk actions ─────────
function ClaimRow({ c, api, onChange }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const pending = c.trade_desk_approval_status === "pending";
  const lodged = c.claim_form?.status === "submitted";
  const uncleared = 6 - Object.values(c.scrutiny || {}).filter((r) => r?.state === "cleared").length;

  const decide = async (decision) => {
    if (decision === "rejected" && !reason.trim()) { toast.error("Reason required."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/indirect-claims/${c.id}/trade-desk-decision`,
        { decision, reason: reason.trim() });
      toast.success(`Claim ${decision}.`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-sm border border-white/10 hover:border-[#C9A961]/40 bg-black/40 p-4 mb-3 transition-colors" data-testid={`ic-row-${c.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 cursor-pointer" onClick={() => setOpen(!open)}>
        <div>
          <div className="font-serif text-[#F5F5F0]">#{c.id.slice(0, 8).toUpperCase()} · {c.order_ref}</div>
          <div className="font-body text-[11px] text-[#8A8A82] mt-1">
            {c.buyer_name} · {inr(c.order_value)} · {c.days_gap}d gap
          </div>
        </div>
        <div className="flex items-center gap-3">
          <SlabBadge pct={c.slab_payout_pct} label={`${c.slab_payout_pct}%`} />
          <span className="font-serif text-lg tabular-nums text-[#F5F5F0]">{inr(c.amount_claimed)}</span>
          <StatusPill status={c.trade_desk_approval_status} />
          {c.trade_desk_approval_status !== "rejected" && (
            <AckPill ack={!!c.buyer_acknowledged} />
          )}
        </div>
      </div>
      {open && (
        <div className="mt-4 pt-3 border-t border-white/10 grid md:grid-cols-2 gap-4">
          <div className="space-y-1 font-body text-[13px] text-[#C9C9C2]">
            <div><b className="text-[#8A8A82]">Buyer:</b> {c.buyer_email}</div>
            <div data-testid={`ic-form-status-${c.id}`}><b className="text-[#8A8A82]">Claim form:</b>{" "}
              {c.status === "customer_submitted" || c.claim_form?.status === "submitted"
                ? <span className="text-[#2FB999] font-semibold">CUSTOMER SUBMITTED{c.claim_form?.submitted_at ? ` · ${c.claim_form.submitted_at.slice(0, 16).replace("T", " ")} UTC` : ""} · {c.claim_form?.documents?.length || 0} document(s)</span>
                : c.claim_form ? <span className="text-[#C9A961]">Link sent {String(c.claim_form.sent_at || "").slice(0, 10)} · {c.claim_form.status === "identity_verified" ? "identity verified, filling" : "awaiting customer"}</span>
                : <span className="text-[#8A8A82]">not issued</span>}
              {c.claim_form?.status !== "submitted" && (
                <button data-testid={`ic-form-resend-${c.id}`} onClick={async () => { try { await api.post(`/admin/indirect-claims/${c.id}/form/resend`); toast.success("Claim form link re-sent"); } catch { toast.error("Could not resend"); } }}
                  className="ml-2 underline text-[#C9A961] hover:text-[#F5F5F0]">resend link</button>
              )}
            </div>
            <div><b className="text-[#8A8A82]">Fulfilled:</b> {c.fulfillment_date} · <b className="text-[#8A8A82]">Applied:</b> {c.applying_date}</div>
            <div><b className="text-[#8A8A82]">Base rate:</b> {c.base_rate_pct}%</div>
            <div><b className="text-[#8A8A82]">Eligible:</b> {inr(c.eligible_commission_inr)} · <b className="text-[#8A8A82]">Deduction:</b> <span className="text-[#C0392B]">{inr(c.deduction_inr)}</span></div>
            <div><b className="text-[#8A8A82]">Slab:</b> {c.slab_label}</div>
            {c.supplier_name && <div><b className="text-[#8A8A82]">Supplier:</b> {c.supplier_name} {c.supplier_gstin ? `(${c.supplier_gstin})` : ""}</div>}
            {c.project_name && <div><b className="text-[#8A8A82]">Project:</b> {c.project_name}</div>}
            {c.note && <div className="italic text-[#8A8A82]">{c.note}</div>}
            <div><b className="text-[#8A8A82]">Authored by:</b> {c.authored_by} at {(c.created_at || "").slice(0, 19)} UTC</div>
            {!pending && (
              <div className="mt-2 text-[12px]"><b className="text-[#8A8A82]">Trade Desk decision:</b>{" "}
                <span style={{ color: c.trade_desk_approval_status === "approved" ? "#2FB999" : "#C0392B" }}>
                  {c.trade_desk_approval_status}
                </span>{" "}
                by {c.trade_desk_decided_by} at {(c.trade_desk_decided_at || "").slice(0, 19)} UTC
                {c.trade_desk_decision_reason ? ` · ${c.trade_desk_decision_reason}` : ""}
              </div>
            )}
          </div>
          {pending && (
            <div>
              <div className="rounded-sm border border-[#C0392B]/40 bg-[#C0392B]/[0.06] p-4">
                <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C0392B]">Trade Desk decision</div>
                {lodged && uncleared > 0 && (
                  <div className="mt-2 font-body text-[11px] text-[#E0A030]" data-testid={`ic-uncleared-warn-${c.id}`}>⚠ {uncleared} of 6 scrutiny checkpoints not yet cleared</div>
                )}
                <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                  data-testid={`ic-reason-${c.id}`}
                  placeholder="Reason (required to reject)"
                  className="input mt-3" />
                <div className="mt-3 flex gap-2">
                  <button onClick={() => decide("approved")} disabled={busy}
                    data-testid={`ic-approve-${c.id}`}
                    className="rounded-sm bg-[#2FB999] hover:bg-[#3ECEB0] text-black px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em] disabled:opacity-40">
                    <ShieldCheck size={12} className="inline mr-1 -mt-0.5" />Approve
                  </button>
                  <button onClick={() => decide("rejected")} disabled={busy}
                    data-testid={`ic-reject-${c.id}`}
                    className="rounded-sm bg-[#C0392B] hover:bg-red-700 text-white px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em] disabled:opacity-40">
                    <XCircle size={12} className="inline mr-1 -mt-0.5" />Reject
                  </button>
                </div>
              </div>
              {lodged && <EvidenceCallPanel c={c} api={api} onChange={onChange} />}
            </div>
          )}
          {lodged && <div className="md:col-span-2"><ScrutinyControl c={c} api={api} onChange={onChange} /></div>}
        </div>
      )}
    </div>
  );
}

export function AdminIndirectClaims({ initialFilter = "" } = {}) {
  const { api } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [claims, setClaims] = useState([]);
  const [filter, setFilter] = useState(initialFilter);
  const [loading, setLoading] = useState(true);

  // If parent switches to us with a new initialFilter, honor it (one-click deep-link
  // from the Overview "Board Escalation Queue" tile).
  useEffect(() => { setFilter(initialFilter); }, [initialFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs =
        filter === "board_escalation" ? "?board_escalation=true"
        : filter === "awaiting_ack" ? "?awaiting_ack=true"
        : filter ? `?status=${filter}`
        : "";
      const [pol, cl] = await Promise.all([
        api.get("/admin/indirect-claims/policy"),
        api.get("/admin/indirect-claims" + qs),
      ]);
      setPolicy(pol.data); setClaims(cl.data.claims || []);
    } catch { toast.error("Could not load indirect claims."); }
    finally { setLoading(false); }
  }, [api, filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-indirect-claims">
      <style>{`.input{width:100%;background:#000;border:1px solid #C9A96130;border-radius:2px;padding:8px 10px;font-family:Inter,sans-serif;font-size:13px;color:#F5F5F0}.input:focus{outline:none;border-color:#C9A961}`}</style>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Indirect Commission Desk</div>
          <h2 className="font-serif text-2xl text-[#F5F5F0]">Past-PO commission claims — admin authored only</h2>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="ic-filter"
            className="input w-56">
            <option value="">All</option>
            <option value="pending">Pending Trade Desk</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="awaiting_ack">Awaiting buyer ack</option>
            <option value="customer_submitted">Customer submitted</option>
            <option value="board_escalation">Board Escalation (0% · &gt;45d)</option>
          </select>
          <button onClick={load} data-testid="ic-refresh"
            className="rounded-sm border border-[#C9A961]/40 text-[#C9A961] px-3 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em] whitespace-nowrap">
            <RefreshCw size={12} className="inline mr-1 -mt-0.5" />Refresh
          </button>
        </div>
      </div>

      <Composer api={api} policy={policy} onCreated={load} />

      {loading ? <div className="font-body text-sm text-[#8A8A82]">Loading…</div> :
       claims.length === 0 ? <div className="font-body text-sm text-[#8A8A82]">No indirect claims yet.</div> :
       claims.map((c) => <ClaimRow key={c.id} c={c} api={api} onChange={load} />)}
    </div>
  );
}
